/**
 * app/router/routes.accept-invite — route registration module for the
 * customer accept-invite page at /einladung/:token.
 *
 * Exports a factory `makeAcceptInviteRoute(parentRoute)` that returns a typed
 * TanStack route without circular-importing router.tsx.
 *
 * Wiring (in app/router.tsx):
 *   import { makeAcceptInviteRoute } from './router/routes.accept-invite';
 *   const customerAcceptInviteRoute = makeAcceptInviteRoute(rootRoute);
 *   // Add to routeTree under rootRoute (public — bypasses auth guard).
 *
 * The route is PUBLIC — lives under rootRoute, NOT appLayoutRoute.
 * Path: /einladung/$token
 *
 * NOTE: JSX is intentionally absent from this .ts file. The component wraps
 * the lazy page in Suspense via React.createElement to avoid requiring .tsx.
 */

import { lazy, Suspense, createElement } from 'react';
import { createRoute } from '@tanstack/react-router';
import type { AnyRoute } from '@tanstack/react-router';

const AcceptInvitePage = lazy(() => import('@/pages/accept-invite'));

/** Path constant — single source of truth for the accept-invite URL pattern. */
export const ACCEPT_INVITE_ROUTE_PATH = '/einladung/$token' as const;

/** Suspense-wrapped component rendered for the accept-invite route. */
function AcceptInviteRouteComponent() {
  return createElement(
    Suspense,
    { fallback: createElement('div', null, 'Lädt…') },
    createElement(AcceptInvitePage),
  );
}

/**
 * Creates the /einladung/$token route under the given parent route.
 * The `any` cast is required because AnyRoute and RootRoute share no
 * assignable interface in this version of TanStack Router.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function makeAcceptInviteRoute(parentRoute: AnyRoute | any) {
  return createRoute({
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    getParentRoute: () => parentRoute,
    path: ACCEPT_INVITE_ROUTE_PATH,
    component: AcceptInviteRouteComponent,
  });
}
